import React, { useState, useEffect } from "react";
import styled from "styled-components";

import AlimtalkLeftSideComponent from "./components/AlimtalkLeftSideComponent";
import AlimtalkMapContainer from "./components/AlimtalkMapContainer";
import LegendComponent from "./components/LegendComponent";
import LoadingComponent from "./components/LoadingComponent";

import "./css/common.css";
import "./css/layout.css";
import { targetCrops } from "./data/fireblightOptionData";
import {
  getFBSpotsAlimtalk,
  getSigunguList,
} from "./context/api_alimtalk";
import {alimtalkLegend} from "./data/alimtalkConst";

const getYearOptions = (minDate, todayDate) => {
  if (!minDate || !todayDate) return [];
  const yearOptions = [];
  const minDateYear = new Date(minDate).getFullYear();
  let currentYear = new Date(todayDate).getFullYear();
  while (minDateYear <= currentYear) {
    yearOptions.push(currentYear);
    currentYear -= 1;
  }
  return yearOptions;
};


const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  background: #2d66af;
  padding: 0 16px;
  height: 50px;
  flex-shrink: 0;
  gap: 12px;
`;

const TopBarTitle = styled.h1`
  color: #fff;
  font-size: 0.95em;
  font-weight: bold;
  margin: 0;
  white-space: nowrap;
`;

const TopBarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.35);
  flex-shrink: 0;
`;

const SearchLabel = styled.span`
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  white-space: nowrap;
`;

const TopBarSelect = styled.select`
  font-size: 13px;
  padding: 3px 8px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  height: 30px;
  cursor: pointer;

  option {
    background: #2d66af;
    color: #fff;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const TableArea = styled.div`
  width: 45%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 2px solid #c8d8ee;
`;

const MapArea = styled.div`
  width: 55%;
  height: 100%;
  box-sizing: border-box;
  position: relative;
`;

const LegendOverlay = styled.div`
  position: absolute;
  bottom: 30px;
  right: 10px;
  z-index: 500;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.4);
  padding: 5px;
`;

function AlimtalkPage() {
  const today = new Date();
  const minDate = new Date("2026-01-01");

  const [error, setError] = useState(null);
  const [loadingCount, setLoadingCount] = useState(0);
  const loading = loadingCount > 0;
  const setLoading = (val) => setLoadingCount((prev) => Math.max(0, prev + (val ? 1 : -1)));
  const [allSpots, setAllSpots] = useState([]);
  const [targetCrop, setTargetCrop] = useState(targetCrops[0]);
  const [targetYear, setTargetYear] = useState(today.getFullYear());
  const [sigunguList, setSigunguList] = useState([{ region: "all", regionName: "전체" }]);
  const [targetRegion, setTargetRegion] = useState({ region: "all", regionName: "전체" });
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);

  useEffect(() => {
    setYearOptions(getYearOptions(minDate, today));
  }, []);

  useEffect(() => {
    getSigunguList(
        (data) => {
          setSigunguList(data);
          setTargetRegion(data[0]);
        },
        ((e) => setError(e)),
        targetCrop,
        targetYear,
    );
  }, [targetYear, targetCrop]);

  useEffect(() => {
    getFBSpotsAlimtalk(
      (data) => setAllSpots(data),
      (val) => setLoading(val),
      (e) => setError(e),
      targetCrop,
      targetYear,
      { region: "all" }
    );
  }, [targetCrop, targetYear]);

  // const displayedSpots = targetRegion?.region === "all"
  //   ? allSpots
  //   : allSpots.filter((s) => s.region === targetRegion?.region);

  const onChangeTargetCrop = (e) => {
    const target = targetCrops.find((item) => item.id == e.target.value);
    if (target) setTargetCrop(target);
  };

  const onChangeYearOption = (e) => {
    setTargetYear(e.target.value);
  };

  const onChangeRegion = (e) => {
    const target = sigunguList.find((item) => item.region === e.target.value);
    if (target) setTargetRegion(target);
  };

  return (
    <>
      {loading ? <LoadingComponent /> : null}
      <PageWrapper>
        <TopBar>
          <TopBarTitle>전국 과수화상병 알림톡 모니터링</TopBarTitle>
          <TopBarDivider />
          <SearchLabel>조회 조건</SearchLabel>
          <TopBarSelect onChange={onChangeYearOption} value={targetYear}>
            {yearOptions.map((year) => (
              <option key={`yearOption-${year}`} value={year}>
                {year}년
              </option>
            ))}
          </TopBarSelect>
          <TopBarSelect onChange={onChangeTargetCrop} value={targetCrop ? targetCrop.id : ""}>
            {targetCrops.map((crop) => (
              <option key={`cropOption-${crop.id}`} value={crop.id}>
                {crop.titleKo}
              </option>
            ))}
          </TopBarSelect>
          <TopBarSelect onChange={onChangeRegion} value={targetRegion ? targetRegion.region : "all"}>
            {sigunguList.map((sigungu) => (
              <option key={`regionOption-${sigungu.region}`} value={sigungu.region}>
                {sigungu.regionName}
              </option>
            ))}
          </TopBarSelect>
        </TopBar>

        <MainContent>
          <TableArea>
            <AlimtalkLeftSideComponent
              setError={setError}
              setLoading={setLoading}
              targetCrop={targetCrop}
              targetYear={targetYear}
              targetRegion={targetRegion}
              selectedSpot={selectedSpot}
              setSelectedSpot={setSelectedSpot}
            />
          </TableArea>

          <MapArea>
            <AlimtalkMapContainer
              totalSpots={allSpots}
              targetRegion={targetRegion}
              selectSpot={(spot) => {
                setSelectedSpot(spot);
                if (spot?.region) {
                  const matched = sigunguList.find((s) => s.region === spot.region);
                  if (matched) setTargetRegion(matched);
                }
              }}
            />
            <LegendOverlay>
              <LegendComponent data={alimtalkLegend} />
            </LegendOverlay>
          </MapArea>
        </MainContent>
      </PageWrapper>
    </>
  );
}

export default AlimtalkPage;
